import axios from 'axios';
import { API_BASE_URL } from './config';
import { triggerSudoModal } from './components/SudoModal';  // R10e36.71.2.6-F814-c
import { toast } from 'sonner';
import { maybeShowAccessNotice } from './accessNotice';  // U2b

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// R11.W4-F1027-R9 Item LL: Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    // R10e36.47r-F204: Sliding JWT refresh - store new token if provided
    const refreshToken = response.headers['x-refresh-token'];
    if (refreshToken) {
      localStorage.setItem('access_token', refreshToken);
    }
    return response;
  },
  async (error) => {
    try { maybeShowAccessNotice(error?.response?.status, error?.response?.data?.detail); } catch (e) { /* ignore */ }
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    
    // R9-002 FIX: Do NOT redirect on 401 for login endpoint
    // The login page handles its own error display via toast
    const isLoginRequest = requestUrl.includes('/auth/login');
    
    // R10e9-005: Also don't redirect on import commit (long-running operation)
    const isImportCommit = requestUrl.includes('/import/') && requestUrl.includes('/commit');
    
    // R10e36.71.2.5-F812: Don't redirect on invalid MFA verify — user needs to retype code
    const isMfaVerify = requestUrl.includes('/auth/mfa/verify');
    
    // R11.W4-F1027-R9 Item LL: Don't treat refresh endpoint failures as session expiry
    const isRefreshRequest = requestUrl.includes('/auth/refresh');
    
    // R10e36.71.2.6-F814-c: Auto-trigger SudoModal with a retry function
    // When sudo-verify succeeds, the modal will invoke the retry, which re-issues the failed request
    const sudoRequired = error.response?.headers?.['x-sudo-required'];
    if (sudoRequired === 'true' && error.response?.status === 403) {
      // Store the failed config as a retry function. The modal's onSuccess handler will invoke it.
      const retryFn = () => apiClient(error.config);
      triggerSudoModal(retryFn);
      return Promise.reject(error);
    }
    
    // R11.W4-F1027-R9 Item MM: Distinguish 401 vs 403 error messages
    if (error.response?.status === 403) {
      // 403 = User IS authenticated but lacks permission
      const detail = error.response?.data?.detail || 'You do not have permission for this action';
      const requestId = error.response?.data?.request_id;
      
      // Don't show toast for sudo-required (modal handles it)
      if (!sudoRequired) {
        const message = requestId 
          ? `${detail} (ref: ${requestId})`
          : detail;
        toast.error(message);
      }
      return Promise.reject(error);
    }
    
    // R11.W4-F1027-R9 Item LL: Handle 401 with token refresh
    if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest && !isImportCommit && !isMfaVerify && !isRefreshRequest) {
      
      // Check if we should try to refresh
      const currentToken = localStorage.getItem('access_token');
      if (!currentToken) {
        // No token, just redirect to login
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        // Another refresh is in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Attempt to refresh the token
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/refresh`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${currentToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data?.access_token) {
          const newToken = response.data.access_token;
          localStorage.setItem('access_token', newToken);

          // Process queued requests
          processQueue(null, newToken);

          // Retry the original request with new token
          originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
          return apiClient(originalRequest);
        }
        // D-ii.8 (2026-09-20): CC#20 — if /auth/refresh returned 200 but
        // no access_token in the body (bug in backend, unexpected shape,
        // or truncated response), the previous code fell through with
        // no processQueue call and no rejection, so every queued
        // request hung forever waiting for a token that never arrived,
        // and callers doing `res.data.x` on the resolved undefined
        // hit "TypeError: Cannot read properties of undefined" — user
        // saw permanent spinner and a white screen. Now: treat empty-
        // body 200 as a refresh failure and route it through the same
        // catch path that a real failure uses.
        const _emptyBodyErr = new Error('Refresh returned no token');
        processQueue(_emptyBodyErr, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        toast.error('Session expired. Please log in again.');
        window.location.href = '/login';
        return Promise.reject(_emptyBodyErr);
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect
        processQueue(refreshError, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        toast.error('Session expired. Please log in again.');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // Fallback: For unhandled 401s, redirect to login
    if (error.response?.status === 401 && !isLoginRequest && !isImportCommit && !isMfaVerify) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
